/**
 * Adds a control to the ol3 map control collection
 * map/commands/map-hover-handler also implements a similar solution specifically for mouse location
 * and it is able to interpret the configuration before creating the map control (which is prefered)
 * TODO: ability to position ol.controls within a ol-control container (e.g. mouse coordinates + zoom in the top-left corner)
 */
import View = require("./view");
import Utils = require("../utils");
import ol = require("openlayers");
import Portal = require("portal-gis");

import _ = require("underscore");
import { LayerSwitcher } from "ol3-layerswitcher/index";
import Input = require("../ol3/control/input");

_.extend(ol.control, {
  LayerSwitcher: LayerSwitcher,
  Input: Input.Control
});

class Control extends View {
  static about = `

Wrapper for openlayers controls.  Documented and undocumented options are passed through to the underlying control.  In cases where the underlying
control cannot accept a string representation of a value you may wrap the option in eval() and use code.

Usage Examples:

[FullScreen](https://openlayers.org/en/latest/apidoc/ol.control.FullScreen.html)

    <CONTROL about="Display the map full screen" disabled="False" id="map-full-screen" mid="app/controls/ol3-control" type="na">
        <OPTIONS>
            <Values about="identify the ol3 constructor/class" id="control-type" value="FullScreen" />
            <Values about="http://openlayers.org/en/latest/apidoc/ol.control.FullScreen.html" id="label" value="⤢" />
        </OPTIONS>
    </CONTROL>

[ZoomToExtent](https://openlayers.org/en/latest/apidoc/ol.control.ZoomToExtent.html)

    <CONTROL about="Restores the map to the original &quot;full&quot; extent" id="map-zoom-to-extent" mid="app/controls/ol3-control">
        <OPTIONS>
            <Values about="identify the ol3 constructor/class" id="control-type" value="ZoomToExtent" />
            <Values about="Using eval to cast the extent as number[]" id="extent" value="eval([-12830000, 4320000,-12820000, 4330000])" />
            <Values about="top-left" id="className" value="ol-zoom-extent" />
        </OPTIONS>
    </CONTROL>

[Input](#app/ol3/control/input)

    <CONTROL about="Creates an 'input' control on the map" id="universal-search-input" mid="app/controls/ol3-control" text="🔍">
        <OPTIONS>
            <Values about="Position the input in the top-left corner, configure the control to keep the expander to the left (via 'right')" id="className" value="ol-control top-2 left-2 ol-input right" />
            <Values about="identify the ol3 constructor/class" id="control-type" value="Input" />
            <Values about="output 'location' instead of 'value'" id="valueName" value="location" />
            <Values about="Start the control already expanded" id="expanded" value="true" />
            <Values about="When user presses enter collapse the control" id="autoCollapse" value="false" />
            <Values about="Text when the control is collapsed" id="closedText" value="🔍" />
            <Values about="Text when the control is expanded (collapses to the left)" id="openedText" value="«" />
            <Values id="placeholderText" value="🔍" />
        </OPTIONS>
        <EVENTS>
            <EVENT about="When the input changes trigger a universal search event" event="change" id="change-handler" mid="app/commands/trigger">
                <OPTIONS>
                    <Values about="When the input changes trigger a universal search" id="event" value="universal-search" />
                </OPTIONS>
            </EVENT>
        </EVENTS>
    </CONTROL>

[ZoomToExtent](https://openlayers.org/en/latest/apidoc/ol.control.ZoomToExtent.html)

    <CONTROL about="Restores the map to the original &quot;full&quot; extent" id="map-zoom-to-extent" mid="app/controls/ol3-control">
        <OPTIONS>
            <Values about="identify the ol3 constructor/class" id="control-type" value="ZoomToExtent" />
            <Values about="experimenting with eval" id="extent" value="eval([-12830000, 4320000,-12820000, 4330000])" />
            <Values about="top-left" id="className" value="ol-zoom-extent" />
        </OPTIONS>
    </CONTROL>

[OverviewMap](https://openlayers.org/en/latest/apidoc/ol.control.OverviewMap.html)

    <CONTROL about="alternative zoom control" id="map-overview-map" mid="app/controls/ol3-control">
        <OPTIONS>
            <Values about="identify the ol3 constructor/class" id="control-type" value="OverviewMap" />
            <Values about="top-left" id="className" value="top-3 right-2 ol-overviewmap" />
            <Values about="Text to display when the map is collapsed" id="label" value="«" />
            <Values about="text to display when the control is expanded (click to collapse)" id="collapseLabel" value="»" />
        </OPTIONS>
    </CONTROL>

[MousePosition](https://openlayers.org/en/latest/apidoc/ol.control.MousePosition.html)

    <CONTROL about="adds mouse coordinates to map, for testing and demonstration of why generic ol3-control is a trade-off" id="map-mouse-position" mid="app/controls/ol3-control">
        <OPTIONS>
            <Values about="identify the ol3 constructor/class" id="control-type" value="MousePosition" />
            <Values about="top-left container" id="className" value="ol-control top-2 right-3 ol-mouse-position" />
            <Values about="see http://openlayers.org/en/latest/apidoc/ol.control.MousePosition.html" id="undefinedHTML" value="X,Y" />
            <Values about="render coordinates using this projection" id="projection" value="EPSG:4326" />
            <Values about="example of why generic control will not work, requires an interpreter like map-hover-handler otherwise no way to specify 'coordinateFormat'" id="coordinateFormat" value="eval(ol.coordinate.createStringXY(5))" />
        </OPTIONS>
    </CONTROL>

`;

  static options = {
    title: {
      about: "button title",
      default: ""
    },
    "css-name": {
      about: "class name to assign to the button"
    },
    "control-type": {
      about: "openlayers control type",
      default: "Control"
    }
  };

  public mapControl: ol.control.Control;

  addButton(command: Portal.CommandLeaf, container?: JQuery) {
    let options = Utils.asDictionary(command.Options.Values);
    let title = options["title"] || "";
    let btn = document.createElement("button");
    btn.className = options["css-name"];
    btn.innerHTML = command.text;
    btn.onclick = () => this.trigger("command", { command: command });
    if (title) btn.title = title;
    this.view.el.appendChild(btn);
  }

  registerEvents(events: Portal.EventsElement) {
    events.Events.forEach(event => {
      this.mapControl.on(event.name, args => {
        Utils.execute(event, this.mediator, args);
      });
    });
    return null;
  }

  broadcast() {
    let options = this.options;

    // TODO: should be taking place in View?
    this.configuration.Commands.Commands.forEach(config => {
      this.commands[config.id] = config;
    });
    this.configuration.Events.Events.forEach(config => {
      this.events[config.id] = config;
    });

    let ol3Class = options["control-type"] || "Control";
    let Constructor = ol.control[ol3Class];

    if (Constructor === ol.control.Control) {
      let commandIds = Object.keys(this.commands);

      if (!commandIds.length) {
        let btn = document.createElement("button");
        btn.className = options["css-name"];
        btn.innerHTML = this.configuration.text;
        btn.onclick = () => this.trigger();
        this.view.el.appendChild(btn);
      }
    }

    // do not broadcast but instead add to the controller via region.show
    let controller = <View>this.args.controller;
    if (controller && controller.addChild) {
      controller.addChild(this);
      options.target = this.view.el;
    } else {
      options.element = this.view.el;
    }

    // convert numbers to numeric, booleans to booleans
    var regex = /^\d+(\.\d+)?$/;
    Object.keys(options).forEach(k => {
      let v = <string | boolean | Number>options[k];
      switch (v) {
        case "true":
          v = true;
          break;
        case "false":
          v = false;
          break;
        default:
          if (typeof v === "string") {
            if (regex.test(v)) v = parseFloat(v);
          }
          break;
      }
      options[k] = <any>v;
    });

    let control = (this.mapControl = Constructor.create
      ? Constructor.create(options)
      : new Constructor(options));
    this.mediator.mapViewMap(map => {
      map.addControl(control);
    });

    if (this.configuration.Events.Events.length) {
      // this is already happening via API.updateController
      // this.registerEvents(this.configuration.Events);
    }
  }
}

export = Control;
